import {
  AUDIO_REANCHOR_THRESHOLD_MS,
  CUE_RESCHEDULE_STEP_MS,
  FIRE_ALARM_HZ,
  IMPACT_TONE_HZ,
  LASER_MARK_LATE_TAIL_MS,
  LASER_TONE_HZ,
  LASING_CALLOUT_AFTER_MS,
  MARK_TONE_MS,
  RAMP_BEEP_MS,
  RAMP_ONSETS_MS,
  SHOT_LATE_AFTER_MS,
  VOICE_LATE_MS,
  WARN_WINDOW_MS,
} from '../constants.js';
import type { Timeline } from './types.js';

export type Role = 'UAV' | 'FDC' | 'OBSERVER' | 'ADMIN';

export type CueId =
  | 'fire-alarm'
  | 'shot-late'
  | 'lasing-callout'
  | 'laser-warn'
  | 'laser-mark'
  | 'laser-callout'
  | 'impact-warn'
  | 'impact-mark';

export type CueKind = 'alarm' | 'alert' | 'ramp' | 'tone' | 'voice';

export interface ScheduledCue {
  id: CueId;
  kind: CueKind;
  /** absolute onset (server epoch ms) — for windowed cues, the window start */
  at: number;
  /** window end for ramps; onset + duration for tones */
  until: number;
  hz?: number;
  text?: string;
  /**
   * SCHEDULED — plays at `at`.
   * LATE_PARTIAL — first seen inside the window / within the late tolerance: enter at `enterAt`.
   * MISSED — the moment passed before this client first saw the timeline: never replayed.
   */
  status: 'SCHEDULED' | 'LATE_PARTIAL' | 'MISSED';
  enterAt?: number;
  lateByMs?: number;
}

const CUE_ROLES: Record<CueId, readonly Role[]> = {
  'fire-alarm': ['UAV', 'OBSERVER'],
  'shot-late': ['UAV', 'OBSERVER'],
  'lasing-callout': ['UAV', 'OBSERVER'],
  'laser-warn': ['UAV', 'OBSERVER'],
  'laser-mark': ['UAV', 'OBSERVER'],
  'laser-callout': ['FDC', 'OBSERVER'],
  'impact-warn': ['UAV', 'FDC', 'OBSERVER'],
  'impact-mark': ['UAV', 'FDC', 'OBSERVER'],
};

/** Beep onsets for a ramp ending at `markAt` (§11.4: accelerating repetition, constant pitch). */
export function rampOnsets(markAt: number): number[] {
  return RAMP_ONSETS_MS.map((o) => markAt + o);
}

export const RAMP_BEEP_DURATION_MS = RAMP_BEEP_MS;

/**
 * Pure cue schedule (§11.4): same (timeline, role, firstSightAt) anywhere ⇒ same absolute schedule.
 * `firstSightAt` is the client's serverNow() when it first saw this timeline (after a refresh, the
 * ledger decides what was already played; this function decides what may still play).
 */
export function computeCueSchedule(tl: Timeline, role: Role, firstSightAt: number): ScheduledCue[] {
  const out: ScheduledCue[] = [];
  const wants = (id: CueId): boolean => CUE_ROLES[id].includes(role);

  const point = (id: CueId, kind: CueKind, at: number, durationMs: number, lateToleranceMs: number, extra: Partial<ScheduledCue>): void => {
    if (!wants(id)) return;
    const late = firstSightAt - at;
    const cue: ScheduledCue = { id, kind, at, until: at + durationMs, status: 'SCHEDULED', ...extra };
    if (late > 0) {
      if (late <= lateToleranceMs) {
        cue.status = 'LATE_PARTIAL';
        cue.enterAt = firstSightAt;
        cue.lateByMs = late;
      } else {
        cue.status = 'MISSED';
        cue.lateByMs = late;
      }
    }
    out.push(cue);
  };

  const window = (id: CueId, markAt: number, hz: number): void => {
    if (!wants(id)) return;
    const at = markAt - WARN_WINDOW_MS;
    const cue: ScheduledCue = { id, kind: 'ramp', at, until: markAt, hz, status: 'SCHEDULED' };
    if (firstSightAt >= markAt) {
      cue.status = 'MISSED';
      cue.lateByMs = firstSightAt - markAt;
    } else if (firstSightAt > at) {
      cue.status = 'LATE_PARTIAL';
      cue.enterAt = firstSightAt;
      cue.lateByMs = firstSightAt - at;
    }
    out.push(cue);
  };

  // fire-alarm at T0, replaced by shot-late when the timeline reached this client > 5 s after T0
  const lateSight = firstSightAt - tl.t0;
  if (wants('fire-alarm')) {
    if (lateSight <= SHOT_LATE_AFTER_MS) {
      point('fire-alarm', 'alarm', tl.t0, 600, SHOT_LATE_AFTER_MS, { hz: FIRE_ALARM_HZ });
    } else {
      out.push({ id: 'fire-alarm', kind: 'alarm', at: tl.t0, until: tl.t0 + 600, hz: FIRE_ALARM_HZ, status: 'MISSED', lateByMs: lateSight });
      if (firstSightAt < tl.tLaserWarn) {
        out.push({ id: 'shot-late', kind: 'alert', at: firstSightAt, until: firstSightAt + 1500, text: 'SHOT — IN FLIGHT', status: 'LATE_PARTIAL', enterAt: firstSightAt, lateByMs: lateSight });
      }
    }
  }

  window('laser-warn', tl.tLaserOn, LASER_TONE_HZ);
  point('laser-mark', 'tone', tl.tLaserOn, MARK_TONE_MS, LASER_MARK_LATE_TAIL_MS, { hz: LASER_TONE_HZ });
  point('laser-callout', 'voice', tl.tLaserOn, 1000, VOICE_LATE_MS, { text: 'LASER ON' });

  // first sight inside LASER_ON_PHASE with the laser-mark more than 3 s past → "LASING"
  if (wants('lasing-callout') && firstSightAt >= tl.tLaserOn + LASING_CALLOUT_AFTER_MS && firstSightAt < tl.tImpact) {
    out.push({ id: 'lasing-callout', kind: 'voice', at: firstSightAt, until: firstSightAt + 1000, text: 'LASING', status: 'LATE_PARTIAL', enterAt: firstSightAt, lateByMs: firstSightAt - tl.tLaserOn });
  }

  window('impact-warn', tl.tImpact, IMPACT_TONE_HZ);
  point('impact-mark', 'tone', tl.tImpact, MARK_TONE_MS, LASER_MARK_LATE_TAIL_MS, { hz: IMPACT_TONE_HZ });

  return out.sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}

export type ReanchorReason = 'PERIODIC' | 'CLOCK_STEP' | 'TIMELINE_CHANGED';

export interface ReanchorInput {
  reason: ReanchorReason;
  /** ms from now to the node's onset (negative = already sounding) */
  timeToOnsetMs: number;
  /** |audioT(t) − scheduledAudioT| for PERIODIC, |Δ| of the step for CLOCK_STEP */
  errorMs: number;
}

/**
 * §5.9 precedence: nodes more than 1 s from onset are rescheduled on a > 15 ms periodic error or any
 * clock step; nodes inside the last second are left alone unless the step exceeds 200 ms or the
 * timeline itself changed.
 */
export function reanchorDecision(i: ReanchorInput): 'KEEP' | 'RESCHEDULE' {
  if (i.reason === 'TIMELINE_CHANGED') return 'RESCHEDULE';
  const lastSecond = i.timeToOnsetMs <= 1000;
  if (i.reason === 'PERIODIC') {
    if (lastSecond) return 'KEEP';
    return i.errorMs > AUDIO_REANCHOR_THRESHOLD_MS ? 'RESCHEDULE' : 'KEEP';
  }
  // CLOCK_STEP
  if (lastSecond) return i.errorMs > CUE_RESCHEDULE_STEP_MS ? 'RESCHEDULE' : 'KEEP';
  return 'RESCHEDULE';
}

export function cueRoles(id: CueId): readonly Role[] {
  return CUE_ROLES[id];
}
